import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Check, CheckCircle2, Lock, LogOut, BookOpen, Menu, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '../ui/Modal';
import { AuditFooterBanner } from '../../features/telemetry/AuditFooterBanner';

export interface SetupStep {
  id: string;
  nome: string;
  rota: string;
  status: 'pendente' | 'parcial' | 'concluida' | 'bloqueada';
  iconeKey: string;
  bloqueadoPor?: string;
}

export interface SetupLayoutProps {
  sections: any[];
  setupGuard: {
    etapas: SetupStep[];
    progresso?: number;
    etapasConcluidas?: number;
    totalEtapas?: number;
  };
}

export function SetupLayout({ sections, setupGuard }: SetupLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const nomeEscola = useAuthStore((s) => s.nomeEscola) || 'sua escola';

  // Sidebar oculto por padrão — abre manualmente via hamburger
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  // Listen to custom event to toggle sidebar
  useEffect(() => {
    const handleToggle = () => setSidebarOpen(s => !s);
    window.addEventListener('toggle-setup-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-setup-sidebar', handleToggle);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      toast.error('Erro ao sair da conta');
    }
  };

  return (
    <div
      className="h-screen w-full overflow-hidden flex relative"
      style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 42%, #3b82f6 100%)',
      }}
    >
      {/* Sidebar Area - Deslizante com transição suave de largura */}
      <aside
        className={`ac-layout-sidebar h-screen shrink-0 transition-all duration-300 ease-in-out border-r border-white/5 flex flex-col ${
          sidebarOpen ? 'w-[240px]' : 'w-0 overflow-hidden border-r-0'
        }`}
        style={{
          background: 'linear-gradient(180deg, #1e3a8a 0%, #2563eb 42%, #3b82f6 100%)',
        }}
      >
        <div className="flex flex-col h-full text-white select-none w-[240px]">
          {/* Brand Area */}
          <div className="p-4 border-b border-white/5 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-extrabold text-sm shadow-[0_0_12px_rgba(59,130,246,0.4)] shrink-0">
                AC
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-white/50 font-bold uppercase tracking-wider leading-none">Setup Inicial</span>
                <span className="text-sm font-black text-white truncate leading-tight mt-0.5">Aluno Conectado</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              title="Fechar menu"
            >
              <ChevronLeft size={18} />
            </button>
          </div>

          {/* Navigation Items from sections */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            {sections.map((section: any, si: number) => (
              <div key={si} className="mb-3">
                {section.title && (
                  <div className="px-3 py-1.5 text-[10px] font-bold text-white/40 uppercase tracking-wider">
                    {section.title}
                  </div>
                )}
                {section.items?.map((item: any, ii: number) => {
                  const currentFull = location.pathname + location.search;
                  const isActive = item.to === '/admin/setup'
                    ? (location.pathname === '/admin/setup' && !location.search)
                    : (currentFull === item.to || location.search === `?step=${item.badgeKey}`);

                  const Icon = item.icon;
                  const etapa = setupGuard?.etapas?.find((e: any) => e.id === item.badgeKey || item.to.includes(`step=${e.id}`));
                  const isConcluida = etapa?.status === 'concluida';

                  return (
                    <Link
                      key={ii}
                      to={item.to}
                      onClick={() => {
                        // Fecha no mobile ao clicar em um item
                        if (window.innerWidth < 1024) setSidebarOpen(false);
                      }}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 mb-0.5 group ${
                        isActive
                          ? 'bg-white/15 text-white font-bold shadow-sm'
                          : 'text-white/70 hover:text-white hover:bg-white/8'
                      }`}
                    >
                      {Icon && <Icon size={16} className={isActive ? 'text-white' : 'text-white/50'} />}
                      <span className="truncate flex-1">{item.label}</span>
                      {isConcluida && (
                        <span 
                          className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm transition-transform duration-150 group-hover:scale-110"
                          title="Etapa Concluída"
                        >
                          <Check size={10} strokeWidth={3.5} />
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* User Info Compact */}
          <div className="px-4 py-3 border-t border-white/5 shrink-0">
            <div className="flex items-center gap-2 mb-3">
              {(() => {
                const cleanedName = user?.nome ? user.nome.replace(/^(Prof\.|Profa\.|Profª|Profº)\s+/i, '') : '';
                const initials = cleanedName
                  ? (cleanedName.split(' ').filter(Boolean).length === 1
                      ? cleanedName.split(' ')[0].substring(0, 2).toUpperCase()
                      : (cleanedName.split(' ')[0][0] + (cleanedName.split(' ').filter(Boolean).slice(-1)[0]?.[0] || '')).toUpperCase())
                  : '??';
                return (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                    {initials}
                  </div>
                );
              })()}
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-bold text-white truncate">{user?.nome || 'Diretor(a)'}</span>
                <span className="text-[10px] text-white/50 truncate">{nomeEscola}</span>
              </div>
            </div>

            <button
              onClick={() => setLogoutConfirmOpen(true)}
              className="flex items-center justify-center gap-1.5 w-full py-2.5 px-3 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors text-xs font-bold"
              title="Sair"
            >
              <LogOut size={14} />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Right Pane Content */}
      <div
        className="relative flex flex-col flex-1 min-h-0 w-full h-full transition-all duration-300 ease-in-out"
      >
        <main
          className="flex-1 flex flex-col overflow-auto p-0"
          style={{ position: 'relative' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Outlet />
          </div>
        </main>
      </div>

      <Modal
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title="Deseja sair da sua conta?"
        size="sm"
        footer={
          <>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setLogoutConfirmOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-danger-solid flex items-center gap-1.5"
              onClick={() => {
                setLogoutConfirmOpen(false);
                handleLogout();
              }}
            >
              <LogOut size={14} />
              <span>Sair da Conta</span>
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3 py-1">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
              <LogOut size={20} />
            </div>
            <div className="flex flex-col gap-1 text-sm text-slate-600">
              <p className="font-semibold text-slate-800 m-0">
                Você será desconectado da sua conta atual.
              </p>
              <p className="m-0 text-xs text-slate-500 leading-relaxed">
                Você poderá voltar e continuar de onde parou quando desejar, bastando fazer login novamente com seu e-mail e sua senha cadastrada.
              </p>
            </div>
          </div>

          <div className="mt-1 p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Seu e-mail de acesso
            </span>
            <span className="text-sm font-semibold text-blue-600 break-all font-mono">
              {user?.email || 'e-mail não identificado'}
            </span>
          </div>
        </div>
      </Modal>

      {/* Banner de Auditoria e Telemetria de Versão no Rodapé (acesso ao Lab) */}
      <AuditFooterBanner />
    </div>
  );
}
